import { Fade } from "@material-ui/core";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Fragment, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useServiceOrder } from "../../contexts/ServiceOrderContext";
import api from "../../services/api";
import {
  formatEquipmentOption,
  isGenericEquipment,
  sameEquipmentId
} from "../../utils/equipmentGeneric";

const servicoArr = [
  "",
  "Frota",
  "Retorno de Locação",
  "Ordem Interna",
  "A faturar",
  "Garantia",
  "SAS - Garantia"
];

// axios responses carry the HTTP status directly; axios errors carry it under `.response`.
const getHttpStatus = (result) => result?.response?.status ?? result?.status;

const getErrorMessage = (result) => {
  const errors = result?.response?.data?.errors;
  const firstKey = errors && Object.keys(errors)[0];
  return firstKey ? errors[firstKey][0] : null;
};

const isRequestSuccessful = (result) => {
  const httpStatus = getHttpStatus(result);
  return httpStatus === 200 || httpStatus === 201;
};

const ServiceOrderForm = ({ visible, onHide, toast, selectedStatus }) => {
  const context = useServiceOrder();
  const { access_type } = useAuth();

  const [serviceType, setServiceType] = useState("");
  const [workshopReceivedDate, setWorkshopReceivedDate] = useState("");
  const [companyId, setCompanyId] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [invoice, setInvoice] = useState("");
  const [hourMeter, setHourMeter] = useState("");
  const [errorDescription, setErrorDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [responsible, setResponsible] = useState("");
  const [comments, setComments] = useState("");
  const [request, setRequest] = useState("");
  const [closeOs, setCloseOs] = useState(false);
  const [cancelOs, setCancelOs] = useState(false);
  const [deadline, setDeadline] = useState("");
  const [machineParada, setMachineParada] = useState("Nao");

  const [empresas, setEmpresas] = useState([{ value: "", label: "" }]);
  const [equipmentId, setEquipmentId] = useState(0);
  const [equipmentName, setEquipmentName] = useState("");
  const [disableEquipment, setDisableEquipment] = useState(true);
  const [selectEquipments, setSelectEquipments] = useState([
    { value: "", label: "" }
  ]);
  const [allEquipments, setAllEquipments] = useState([]);
  const [appropriationId, setAppropriationId] = useState(null);
  const [appropriationOptions, setAppropriationOptions] =
    useState([]);
  const [selectedEquipmentIsGeneric, setSelectedEquipmentIsGeneric] =
    useState(false);

  useEffect(() => {
    if (!visible) return;

    getCompanies();
    if (access_type === "Admin") {
      getAllEquipments();
    }
  }, [access_type, visible, serviceType]);

  useEffect(() => {
    setSelectedItemValues();
  }, [context.selectedItem, visible]);

  const syncAppropriation = (
    selectedEquipmentId,
    equipmentsData = allEquipments,
    savedAppropriationId = null,
    selectedEquipmentOverride = null,
    savedAppropriationEquipment = null
  ) => {
    if (!selectedEquipmentId) {
      setSelectedEquipmentIsGeneric(false);
      setAppropriationId(null);
      setAppropriationOptions([]);
      return;
    }

    const normalizedId = Number(selectedEquipmentId);
    const selectedEquipment =
      selectedEquipmentOverride ||
      selectEquipments.find((equip) => sameEquipmentId(equip.value, normalizedId))
        ?.raw ||
      equipmentsData.find((equip) => sameEquipmentId(equip.id, normalizedId));

    if (!selectedEquipment) {
      setSelectedEquipmentIsGeneric(false);
      setAppropriationId(normalizedId);
      setAppropriationOptions([]);
      return;
    }

    const isGeneric = isGenericEquipment(selectedEquipment);
    setSelectedEquipmentIsGeneric(isGeneric);

    if (isGeneric) {
      const machineOptions = equipmentsData
        .filter(
          (equip) =>
            !sameEquipmentId(equip.id, normalizedId) &&
            !isGenericEquipment(equip)
        )
        .map(formatEquipmentOption);

      const resolvedAppropriationId = savedAppropriationId
        ? Number(savedAppropriationId)
        : null;

      if (
        resolvedAppropriationId &&
        savedAppropriationEquipment &&
        !machineOptions.some((option) =>
          sameEquipmentId(option.value, resolvedAppropriationId)
        )
      ) {
        machineOptions.unshift(
          formatEquipmentOption(savedAppropriationEquipment)
        );
      }

      setAppropriationOptions(machineOptions);
      setAppropriationId(resolvedAppropriationId);
      return;
    }

    const selectedOption = formatEquipmentOption(selectedEquipment);
    setAppropriationOptions([selectedOption]);
    setAppropriationId(normalizedId);
  };

  const getAppropriationLabel = () => {
    const locked = Boolean(equipmentId) && !selectedEquipmentIsGeneric;

    if (locked) {
      return equipmentName;
    }

    const fromOptions = appropriationOptions.find((option) =>
      sameEquipmentId(option.value, appropriationId)
    )?.label;

    if (fromOptions) {
      return fromOptions;
    }

    const savedAppropriation = context.selectedItem?.appropriation_equipment;
    if (
      savedAppropriation &&
      sameEquipmentId(savedAppropriation.id, appropriationId)
    ) {
      return formatEquipmentOption(savedAppropriation).label;
    }

    return "";
  };

  const setSelectedItemValues = async () => {
    if (!visible) return;

    if (!context.selectedItem || Object.keys(context.selectedItem).length === 0)
      return applyDefaultValues();

    const item = context.selectedItem;
    const editId = item.id;

    setServiceType(item.service_type);
    // Sempre desmarcado ao abrir: marcar = "Fechar OS" (se ativa) ou
    // "Reabrir OS" (se já fechada) — nunca representa o estado atual.
    setCloseOs(false);
    setCancelOs(false);
    setWorkshopReceivedDate(new Date(item.workshop_received_date));
    setCompanyId(item.company_id);
    setCompanyName(item?.company?.name);
    setEquipmentName(
      item?.equipment ? formatEquipmentOption(item.equipment).label : ""
    );
    setEquipmentId(item.equipment_id);
    setInvoice(item.invoice);
    setHourMeter(item.hour_meter);
    setErrorDescription(item.error_description);
    setPhone(item.phone);
    setEmail(item.email);
    setResponsible(item.responsible);
    setComments(item.comments);
    setRequest(item.request);
    if (item.deadline) {
      setDeadline(new Date(item.deadline));
    }
    setMachineParada(item.machine_stopped === 1 ? "Sim" : "Nao");
    setDisableEquipment(false);

    if (access_type === "Admin") {
      syncAppropriation(
        item.equipment_id,
        allEquipments,
        item.appropriation_equipment_id,
        item.equipment,
        item.appropriation_equipment
      );
    }

    const equipmentsDataPromise =
      access_type === "Admin" && allEquipments.length > 0
        ? Promise.resolve(allEquipments)
        : access_type === "Admin"
        ? fetchAllEquipments()
        : Promise.resolve([]);

    await Promise.all([
      getEquipments(item.company_id),
      equipmentsDataPromise
    ]).then(([, equipmentsData]) => {
      if (editId !== context.selectedItem?.id) return;

      if (access_type === "Admin") {
        syncAppropriation(
          item.equipment_id,
          equipmentsData,
          item.appropriation_equipment_id,
          item.equipment,
          item.appropriation_equipment
        );
      }
    });
  };

  const getCompanies = () => {
    api.get("companies/all").then((response) => {
      let x = [];
      x.push(
        ...response.data.map((empresa) => ({
          value: empresa.company_id || empresa.id,
          label: empresa.name
        }))
      );
      setEmpresas(x);
    });
  };

  const onClose = () => {
    applyDefaultValues();
    onHide();
  };

  const applyDefaultValues = () => {
    setServiceType("");
    setWorkshopReceivedDate("");
    setCompanyName("");
    setCompanyId(0);
    setEquipmentName("");
    setEquipmentId(0);
    setDisableEquipment(true);
    setInvoice("");
    setHourMeter("");
    setErrorDescription("");
    setPhone("");
    setEmail("");
    setResponsible("");
    setComments("");
    setRequest("");
    setCloseOs(false);
    setCancelOs(false);
    setDeadline("");
    setMachineParada("Nao");
    setAppropriationId(null);
    setAppropriationOptions([]);
    setSelectedEquipmentIsGeneric(false);
  };

  const create = async () => {
    context.setFormModalOpened(false);

    const workshopReceivedDatePayload = workshopReceivedDate
      ? new Date(workshopReceivedDate)
      : null;

    const deadlineDatePayload = deadline ? new Date(deadline) : null;

    const payload = {
      service_type: serviceType,
      workshop_received_date: workshopReceivedDatePayload
        ? workshopReceivedDatePayload?.toISOString()?.slice(0, 10)
        : "",
      deadline: deadlineDatePayload
        ? deadlineDatePayload?.toISOString()?.slice(0, 10)
        : "",
      company_id: companyId,
      equipment_id: equipmentId,
      invoice,
      hour_meter: hourMeter,
      error_description: errorDescription,
      phone,
      email,
      responsible,
      comments,
      request,
      close_os: closeOs,
      machine_stopped: machineParada === "Sim" ? 1 : 0
    };

    // Cancelar (ex.: OS aberta em duplicidade) não passa pela trava de
    // fechamento; o backend exige o motivo em Observações.
    if (cancelOs) {
      payload.status = "Cancelado";
      payload.close_os = false;
    }

    if (
      access_type === "Admin" &&
      appropriationId &&
      selectedEquipmentIsGeneric
    ) {
      payload.appropriation_equipment_id = appropriationId;
    }

    let response = null;

    if (context.selectedItem && Object.keys(context.selectedItem).length > 0) {
      response = await context.update(payload, context.selectedItem.id);
    } else {
      response = await context.create(payload);
    }

    const httpStatus = getHttpStatus(response);

    if (httpStatus === 201 || httpStatus === 200) {
      toast.current.show({
        severity: "success",
        summary: "",
        detail: "Ordem de serviço salva com sucesso!",
        life: 5000
      });
      onClose();
      context.getFromApi({ status: selectedStatus });
    } else if (httpStatus === 422) {
      context.setFormModalOpened(true);
      toast.current.show({
        severity: "error",
        summary: "",
        detail: getErrorMessage(response) || "Verifique os campos obrigatorios.",
        life: 5000
      });
    } else {
      context.setFormModalOpened(true);
      toast.current.show({
        severity: "warn",
        summary: "",
        detail: "Erro ao criar ordem de serviço.",
        life: 5000
      });
    }
  };

  const footer = (
    <Fragment>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text"
        onClick={onClose}
      />
      <Button
        label={"Salvar"}
        icon="pi pi-check"
        className="p-button-text"
        onClick={create}
      />
    </Fragment>
  );

  const handleChangeServico = (option) => {
    if (option.value === "Frota") applyDefaultValues();

    setServiceType(option.value);
  };

  const handleChangeMachineParada = (option) => {
    setMachineParada(option.value);
  };

  const handleChangeSelect = async (selectedOption) => {
    setEquipmentId("");
    setEquipmentName("");
    setDisableEquipment(true);
    setAppropriationId(null);
    setAppropriationOptions([]);
    setSelectedEquipmentIsGeneric(false);

    setCompanyId(selectedOption.value);
    empresas.forEach((e) => {
      if (e.value === selectedOption.value) {
        setCompanyName(e.label);
        return;
      }
    });

    await getEquipments(selectedOption.value);
  };

  const getEquipments = async (id_empresa = null) => {
    if (!id_empresa) {
      return;
    }
    await api
      .get("/equipments/getEquipmentsForServiceCall?company_id=" + id_empresa)
      .then((r) => {
        let arr = [];

        arr.push(
          ...r.data.map((equip) => ({
            ...formatEquipmentOption(equip),
            raw: equip
          }))
        );

        setSelectEquipments(arr);
      });

    setDisableEquipment(false);
  };

  const fetchAllEquipments = async () => {
    const response = await api.get("/equipments/all");
    setAllEquipments(response.data);
    return response.data;
  };

  const getAllEquipments = async () => {
    try {
      await fetchAllEquipments();
    } catch {
      setAllEquipments([]);
    }
  };

  const templateSelectPrime = (option) => {
    return <div style={{ maxWidth: "500px" }}>{option.label}</div>;
  };

  const handleChangeSelectEquipment = async (selectedOption) => {
    const selectedId = Number(selectedOption.value);
    setEquipmentId(selectedId);
    selectEquipments.forEach((e) => {
      if (sameEquipmentId(e.value, selectedId)) {
        setEquipmentName(e.label);
        return;
      }
    });

    const selectedFromDropdown = selectEquipments.find((e) =>
      sameEquipmentId(e.value, selectedId)
    );

    if (access_type === "Admin") {
      syncAppropriation(
        selectedId,
        allEquipments,
        null,
        selectedFromDropdown?.raw || null
      );
    }
  };

  const isAppropriationLocked =
    Boolean(equipmentId) && !selectedEquipmentIsGeneric;

  const appropriationLabel = getAppropriationLabel();

  // "Equipamento liberado" é o status que libera o fechamento no backend
  // (ver ServiceOrderRepository::assertCanBeClosed). Já reflete o último
  // atendimento sozinho, porque o backend recalcula o status a cada
  // relatório enviado — um relatório antigo liberado não conta mais se um
  // atendimento posterior mudou o status pra outra coisa.
  const isEquipmentLiberado =
    context.selectedItem?.status === "Equipamento liberado";

  return (
    <Dialog
      visible={visible}
      style={{ marginTop: 60, width: "80rem" }}
      header={"Ordem de serviço"}
      modal
      className="p-fluid"
      footer={footer}
      onHide={onClose}
    >
      <div className={"container pt-2"}>
        <Fade in={true}>
          <div className="container">
            <div className={"row"}>
              <div className="col-md-12 d-flex justify-content-md-end justify-content-sm-center align-items-center">
                <form
                  className={
                    "d-flex flex-column col-md-12 shadow rounded p-4 justify-content-center align-content-center"
                  }
                >
                  <div className={"row pb-1"}>
                    <div className={"col-md-6 pb-3 d-flex flex-column"}>
                      <label htmlFor="MaquinaParada">
                        Máquina está parada?:
                      </label>
                      <Dropdown
                        value={machineParada}
                        options={["Sim", "Nao"]}
                        onChange={handleChangeMachineParada}
                        style={{ height: "50px", alignItems: "center" }}
                      />
                    </div>

                    <div className={"col-md-6 pb-3 d-flex flex-column"}>
                      <label htmlFor="Servico">Serviço:</label>
                      <Dropdown
                        value={serviceType}
                        options={servicoArr}
                        onChange={handleChangeServico}
                        style={{ height: "50px", alignItems: "center" }}
                      />
                    </div>
                  </div>
                  <div className={"row pb-1"}>
                    <div className={"col-md-6 pb-3 d-flex flex-column"}>
                      <label htmlFor="Empresa">Empresa: </label>
                      <Dropdown
                        value={companyName}
                        itemTemplate={templateSelectPrime}
                        valueTemplate={companyName}
                        options={empresas}
                        onChange={handleChangeSelect}
                        filter
                        optionLabel={"label"}
                        optionValue={"value"}
                        placeholder="Selecione a empresa"
                        style={{ height: "50px", alignItems: "center" }}
                      />
                    </div>

                    <div className={"col-md-6 pb-3 d-flex flex-column"}>
                      <label htmlFor="Empresa">Equipamento: </label>
                      <Dropdown
                        value={equipmentName}
                        itemTemplate={templateSelectPrime}
                        disabled={disableEquipment}
                        valueTemplate={equipmentName}
                        options={selectEquipments}
                        onChange={handleChangeSelectEquipment}
                        filter
                        optionLabel={"label"}
                        optionValue={"value"}
                        placeholder="Selecione um equipamento"
                        style={{ height: "50px", alignItems: "center" }}
                      />
                    </div>
                  </div>
                  {access_type === "Admin" && (
                  <div className={"row pb-1"}>
                    <div className={"col-md-12 pb-3 d-flex flex-column"}>
                      <label htmlFor="ApropriacaoGerencial">
                        Apropriação Gerencial:
                      </label>
                      <Dropdown
                        value={
                          isAppropriationLocked
                            ? equipmentName
                            : appropriationId
                        }
                        itemTemplate={templateSelectPrime}
                        valueTemplate={
                          isAppropriationLocked
                            ? equipmentName
                            : appropriationLabel
                        }
                        options={appropriationOptions}
                        onChange={(e) => setAppropriationId(e.value)}
                        disabled={isAppropriationLocked || !equipmentId}
                        filter={selectedEquipmentIsGeneric}
                        showClear={selectedEquipmentIsGeneric}
                        optionLabel={"label"}
                        optionValue={"value"}
                        placeholder={
                          equipmentId
                            ? selectedEquipmentIsGeneric
                              ? "Selecione a máquina vinculada"
                              : equipmentName
                            : "Selecione um equipamento primeiro"
                        }
                        style={{ height: "50px", alignItems: "center" }}
                      />
                      <small className="text-muted mt-1">
                        {selectedEquipmentIsGeneric
                          ? "Patrimônio genérico: selecione a máquina de destino."
                          : "Equipamento convencional: a apropriação gerencial repete o equipamento selecionado."}
                      </small>
                    </div>
                  </div>
                  )}
                  <div className={"row pb-1"}>
                    <div className={"pb-3 d-flex flex-column col-md-4"}>
                      <label htmlFor="dataAtendimento">
                        Data do recebimento na oficina:
                      </label>
                      <Calendar
                        name={"dataAtendimento"}
                        onFocus={() => setWorkshopReceivedDate(new Date())}
                        value={workshopReceivedDate}
                        onChange={(e) => setWorkshopReceivedDate(e.value)}
                        mask="99/99/9999"
                      />
                    </div>
                    {serviceType !== "Frota" && (
                      <>
                        <div className={"col-md-4 pb-3"}>
                          <label htmlFor="invoice">Nota fiscal </label>
                          <InputText
                            name="invoice"
                            value={invoice}
                            autoComplete={"off"}
                            onChange={(e) => setInvoice(e.target.value)}
                          />
                        </div>

                        <div className={"col-md-4 pb-3"}>
                          <label htmlFor="invoice">Horimetro </label>
                          <InputText
                            name="hourMeter"
                            value={hourMeter}
                            onChange={(e) =>
                              setHourMeter(e.target.value.replace(/\D/g, ""))
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className={"row pb-1"}>
                    <div className={"col-md-12 pb-3 d-flex flex-column"}>
                      <label htmlFor="Descrição do problema">
                        Descrição do problema:
                      </label>
                      <InputTextarea
                        style={{ maxWidth: "100%", height: "100px" }}
                        value={errorDescription}
                        onChange={(e) => setErrorDescription(e.target.value)}
                      />
                    </div>
                  </div>
                  {serviceType !== "Frota" && (
                    <div className={"row pb-1"}>
                      <div className={"col-md-4 pb-3"}>
                        <label htmlFor="invoice">Telefone </label>
                        <InputText
                          name="phone"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                      <div className={"col-md-4 pb-3"}>
                        <label htmlFor="email">Email </label>
                        <InputText
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>

                      <div className={"col-md-4 pb-3"}>
                        <label htmlFor="email">
                          Reponsável pelo equipamento{" "}
                        </label>
                        <InputText
                          name="responsible"
                          value={responsible}
                          onChange={(e) => setResponsible(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  <div className={"col-md-12 pb-3 d-flex flex-column"}>
                    <label htmlFor="Descrição do problema">Observações:</label>
                    <InputTextarea
                      style={{ maxWidth: "100%", height: "50px" }}
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                    />
                  </div>

                  <div className={"row pb-1"}>
                    <div className={"col-md-6 pb-3 d-flex flex-column"}>
                      <label>Requisição:</label>
                      <InputTextarea
                        style={{ maxWidth: "100%", height: "50px" }}
                        value={request}
                        onChange={(e) => setRequest(e.target.value)}
                      />
                    </div>

                    <div className={"col-md-6 pb-3 d-flex flex-column"}>
                      <label htmlFor="Deadline">Previsão de entrega:</label>
                      <Calendar
                        name={"Deadline"}
                        onFocus={() => setDeadline(new Date())}
                        value={deadline}
                        onChange={(e) => setDeadline(e.value)}
                        mask="99/99/9999"
                      />
                    </div>
                  </div>

                  {context.selectedItem &&
                    context.selectedItem.status === "Cancelado" && (
                    <div className={"col-md-12"}>
                      <small className="text-danger">
                        OS cancelada. O motivo está em Observações.
                      </small>
                    </div>
                  )}

                  {context.selectedItem &&
                    Object.keys(context.selectedItem).length > 0 &&
                    context.selectedItem.status !== "Cancelado" && (
                    <div
                      className={
                        "col-md-3 d-flex flex-column align-items-center justify-content-center"
                      }
                    >
                      <div className="form-check d-flex flex-row align-items-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={closeOs}
                          id="flexCheckDefault"
                          onChange={() => {
                            setCloseOs(!closeOs);
                            setCancelOs(false);
                          }}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="flexCheckDefault"
                        >
                          {context.selectedItem.status === "Fechado"
                            ? "Reabrir OS"
                            : "Fechar OS"}
                        </label>
                      </div>
                      {closeOs &&
                        context.selectedItem.status === "Pendente peça" && (
                        <small className="text-danger mt-1 text-center">
                          OS não pode ser fechada: há peça pendente.
                        </small>
                      )}
                      {closeOs &&
                        context.selectedItem.status !== "Fechado" &&
                        context.selectedItem.status !== "Pendente peça" &&
                        !isEquipmentLiberado && (
                        <small className="text-danger mt-1 text-center">
                          OS/Equipamento não foi finalizado pelo técnico.
                        </small>
                      )}
                      {context.selectedItem.status === "Fechado" &&
                        closeOs && (
                        <small className="text-warning mt-1 text-center">
                          OS será reaberta como "Aguardando técnico" ao salvar.
                        </small>
                      )}
                      {context.selectedItem.status !== "Fechado" && (
                        <div className="form-check d-flex flex-row align-items-center mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={cancelOs}
                            id="cancelOsCheckbox"
                            onChange={() => {
                              setCancelOs(!cancelOs);
                              setCloseOs(false);
                            }}
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="cancelOsCheckbox"
                          >
                            Cancelar OS
                          </label>
                        </div>
                      )}
                      {cancelOs && (
                        <small className="text-warning mt-1 text-center">
                          Informe o motivo em Observações (ex.: abertura em
                          duplicidade). O cancelamento não pode ser desfeito.
                        </small>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </Fade>
      </div>
    </Dialog>
  );
};

export default ServiceOrderForm;
